// =============================================================================
// popularOddsAdapter — convert /odds/{eventId} payload → VideoOddsStripMarket[]
// =============================================================================
// Engine ships `videoOddsStrip` directly on `VideoPackage` but NOT on plain
// text articles. To use the same `<PopularOddsWidget>` on text articles, we
// read the same /odds/{eventId} payload the rest of the site already uses
// and pick the four "popular" markets in the shape the strip expects:
//
//   1X2            → three-way     (match winner)
//   Over/Under 2.5 → two-way-total (prefer line 2.5)
//   BTTS           → two-way-yesno
//   Asian Hcp      → two-way-ah    (most balanced line)
//
// All user-facing strings are i18n keys — engine ships `defaultMarketId` only;
// labels here use English fallbacks via `defaultValue` so legacy tenants
// without i18n bundles still render readable copy. P10 will populate the
// bundles per locale.
// =============================================================================

import type {
  VideoOddsStripMarket,
  MatchOdds,
  OddsOffer,
  OddsOutcome,
} from '@aamp/shared-types';

interface AdapterStrings {
  /** Match winner (1X2). Default: "Match winner". */
  matchWinner: string;
  /** BTTS title. Default: "Both teams to score". */
  btts: string;
  /** BTTS sublabel. Default: "BTTS — Yes/No". */
  bttsSub: string;
  /** Yes/No labels. Defaults: "Yes" / "No". */
  yes: string;
  no: string;
  /** Over X.X / Under X.X — `{line}` formatted with `decimalSeparator`. */
  over: (line: string) => string;
  under: (line: string) => string;
  /** Title for total-goals market. Default: "Over/Under {line}". */
  totalGoals: (line: string) => string;
  /** Sublabel for total-goals market. Default: "Line {line}". */
  totalGoalsSub: (line: string) => string;
  /** Asian-handicap title. Default: "AH {sign}{line}". */
  asianHandicap: (signedLine: string) => string;
  /** Asian-handicap sublabel. */
  asianHandicapSub: (signedLine: string) => string;
  /** Asian-handicap fallback labels when participant names aren't usable. */
  homeFallback: string;
  awayFallback: string;
  /** Double chance title (#409 Phase 5b). Default: "Double chance". */
  doubleChance: string;
  /** Double chance sublabel. Default: "1X / 12 / X2". */
  doubleChanceSub: string;
}

const TIMESTAMP_FALLBACK = new Date().toISOString();

function priceFromOdds(odds: number | null): number | null {
  // provider raw price = decimal × 1000.
  if (odds === null || !Number.isFinite(odds)) return null;
  return Math.round(odds * 1000);
}

function outcomesAsPriceMap(outcomes: OddsOutcome[]): Record<string, number> {
  const map: Record<string, number> = {};
  for (const o of outcomes) {
    const p = priceFromOdds(o.odds);
    if (p !== null) map[String(o.id)] = p;
  }
  return map;
}

function formatLine(line: number, decimalSeparator: '.' | ','): string {
  const s = String(line);
  return decimalSeparator === ',' ? s.replace('.', ',') : s;
}

function pickMatch1x2(
  offer: OddsOffer | undefined,
  s: AdapterStrings,
): VideoOddsStripMarket | null {
  if (!offer || offer.outcomes.length < 3 || offer.suspended) return null;
  const home = offer.outcomes.find((o) => o.type === 'OT_ONE');
  const draw = offer.outcomes.find((o) => o.type === 'OT_CROSS');
  const away = offer.outcomes.find((o) => o.type === 'OT_TWO');
  if (!home || !draw || !away) return null;
  const ordered = [home, draw, away];
  return {
    id: 'match_winner',
    template: 'three-way',
    label: s.matchWinner,
    oddsRef: {
      eventId: '',
      outcomeIds: ordered.map((o) => o.id),
      labels: {
        '1': home.participantName ?? '1',
        X: 'X',
        '2': away.participantName ?? '2',
      },
    },
    snapshotPrices: {
      takenAt: TIMESTAMP_FALLBACK,
      prices: outcomesAsPriceMap(ordered),
    },
  };
}

function pickBtts(
  offers: OddsOffer[],
  s: AdapterStrings,
): VideoOddsStripMarket | null {
  const offer = offers.find(
    (o) =>
      !o.suspended &&
      o.outcomes.some((x) => x.type === 'OT_YES') &&
      o.outcomes.some((x) => x.type === 'OT_NO'),
  );
  if (!offer) return null;
  const yes = offer.outcomes.find((o) => o.type === 'OT_YES');
  const no = offer.outcomes.find((o) => o.type === 'OT_NO');
  if (!yes || !no) return null;
  const ordered = [yes, no];
  return {
    id: 'btts',
    template: 'two-way-yesno',
    label: s.btts,
    sublabel: s.bttsSub,
    oddsRef: {
      eventId: '',
      outcomeIds: ordered.map((o) => o.id),
      labels: { yes: s.yes, no: s.no },
    },
    snapshotPrices: {
      takenAt: TIMESTAMP_FALLBACK,
      prices: outcomesAsPriceMap(ordered),
    },
  };
}

function pickOverUnder(
  offers: OddsOffer[],
  s: AdapterStrings,
  decimalSeparator: '.' | ',',
): VideoOddsStripMarket | null {
  if (offers.length === 0) return null;
  const withLine = offers
    .filter((o) => !o.suspended && o.outcomes.length >= 2)
    .map((o) => ({ offer: o, line: o.outcomes[0]?.line ?? null }))
    .filter((x): x is { offer: OddsOffer; line: number } => x.line !== null);
  if (withLine.length === 0) return null;
  withLine.sort((a, b) => Math.abs(a.line - 2.5) - Math.abs(b.line - 2.5));
  const first = withLine[0];
  if (!first) return null;
  const offer = first.offer;
  const line = first.line;
  const over = offer.outcomes.find((o) => o.type === 'OT_OVER');
  const under = offer.outcomes.find((o) => o.type === 'OT_UNDER');
  if (!over || !under) return null;
  const ordered = [over, under];
  const formatted = formatLine(line, decimalSeparator);
  return {
    id: `total_goals_${String(line).replace('.', '_')}`,
    template: 'two-way-total',
    label: s.totalGoals(formatted),
    sublabel: s.totalGoalsSub(formatted),
    oddsRef: {
      eventId: '',
      outcomeIds: ordered.map((o) => o.id),
      labels: { over: s.over(formatted), under: s.under(formatted) },
    },
    snapshotPrices: {
      takenAt: TIMESTAMP_FALLBACK,
      prices: outcomesAsPriceMap(ordered),
    },
  };
}

function pickAsianHandicap(
  offers: OddsOffer[],
  homeName: string,
  awayName: string,
  s: AdapterStrings,
  decimalSeparator: '.' | ',',
): VideoOddsStripMarket | null {
  if (offers.length === 0) return null;
  const candidates = offers.filter((o) => !o.suspended && o.outcomes.length >= 2);
  if (candidates.length === 0) return null;
  candidates.sort((a, b) => {
    const aSpread = Math.abs((a.outcomes[0]?.odds ?? 99) - (a.outcomes[1]?.odds ?? 99));
    const bSpread = Math.abs((b.outcomes[0]?.odds ?? 99) - (b.outcomes[1]?.odds ?? 99));
    return aSpread - bSpread;
  });
  const offer = candidates[0];
  if (!offer) return null;

  const home = offer.outcomes.find(
    (o) => o.participantName === homeName || o.label?.includes(homeName),
  );
  const away = offer.outcomes.find(
    (o) => o.participantName === awayName || o.label?.includes(awayName),
  );
  if (!home || !away) {
    const [a, b] = offer.outcomes;
    if (!a || !b) return null;
    return {
      id: 'ah',
      template: 'two-way-ah',
      label: s.asianHandicap(''),
      sublabel: offer.criterion,
      oddsRef: {
        eventId: '',
        outcomeIds: [a.id, b.id],
        labels: {
          home: a.label ?? a.participantName ?? s.homeFallback,
          away: b.label ?? b.participantName ?? s.awayFallback,
        },
      },
      snapshotPrices: {
        takenAt: TIMESTAMP_FALLBACK,
        prices: outcomesAsPriceMap([a, b]),
      },
    };
  }
  const ordered = [home, away];
  const line = home.line ?? 0;
  const sign = line > 0 ? '+' : '';
  const signedLine = `${sign}${formatLine(line, decimalSeparator)}`;
  return {
    id: `ah_${String(line).replace('.', '_').replace('-', 'minus_')}`,
    template: 'two-way-ah',
    label: s.asianHandicap(signedLine),
    sublabel: s.asianHandicapSub(signedLine),
    oddsRef: {
      eventId: '',
      outcomeIds: ordered.map((o) => o.id),
      labels: {
        home: home.label ?? home.participantName ?? s.homeFallback,
        away: away.label ?? away.participantName ?? s.awayFallback,
      },
    },
    snapshotPrices: {
      takenAt: TIMESTAMP_FALLBACK,
      prices: outcomesAsPriceMap(ordered),
    },
  };
}

interface PopularOddsResult {
  markets: VideoOddsStripMarket[];
  defaultMarketId: string;
}

/**
 * Build the popular markets shown by `<PopularOddsWidget>` from a
 * /odds/{eventId} payload. Returns up to four markets (1X2, O/U, BTTS, AH);
 * each is independently optional — if engine has no balanced AH, that slot
 * is dropped rather than holding everything back.
 *
 * `strings` carries i18n-resolved labels so this stays a pure function.
 */
export function popularOddsFromMatchOdds(
  matchOdds: MatchOdds,
  strings: AdapterStrings,
  decimalSeparator: '.' | ',' = '.',
): PopularOddsResult {
  const home = matchOdds.event.homeTeam;
  const away = matchOdds.event.awayTeam;
  const eventId = matchOdds.eventId;

  const markets: VideoOddsStripMarket[] = [];

  const m1x2 = pickMatch1x2(matchOdds.markets.match1x2[0], strings);
  if (m1x2) {
    m1x2.oddsRef.eventId = eventId;
    markets.push(m1x2);
  }

  const ou = pickOverUnder(matchOdds.markets.overUnder, strings, decimalSeparator);
  if (ou) {
    ou.oddsRef.eventId = eventId;
    markets.push(ou);
  }

  const btts = pickBtts(matchOdds.markets.yesNo, strings);
  if (btts) {
    btts.oddsRef.eventId = eventId;
    markets.push(btts);
  }

  const ah = pickAsianHandicap(
    matchOdds.markets.asianHandicap,
    home,
    away,
    strings,
    decimalSeparator,
  );
  if (ah) {
    ah.oddsRef.eventId = eventId;
    markets.push(ah);
  }

  return {
    markets,
    defaultMarketId: markets[0]?.id ?? '',
  };
}

// ---------------------------------------------------------------------------
// Removed 2026-05-22 (Phase 5c step 3):
//   pickDoubleChance, pickByCatalogMarketId, CatalogMarketId
// were the Phase 5b render-side picker for the multi-market kamp-side. They
// are gone — the engine now picks the odds card per section based on
// `cms.markets_catalog.odds_template + odds_line_selector` (migration 0060)
// and ships fully-baked `VideoOddsStripMarket` objects in the
// /market-layouts response. See apps/onsite-engine/src/services/odds/
// odds-picker.ts for the canonical picker.
//
// PopularOddsWidget (article + video pages) still uses
// popularOddsFromMatchOdds above — the article-page "popular markets"
// concept is a separate 4-market surface and stays render-side for now.
// Future cleanup: migrate PopularOddsWidget to consume an engine-shipped
// `popularOdds` block on the article/video package, deleting this whole
// adapter module.
// ---------------------------------------------------------------------------

export type { AdapterStrings };
